import {
  Block,
  Column,
  DelayedSpinner,
  EmptyScreen,
  UploadFileButton
} from '@cdab/scania/qpr/components/atoms'
import { AttachmentList } from '@cdab/scania/qpr/components/molecules'
import { useDeviation } from '@cdab/scania/qpr/contexts/deviation'
import { useCssVariableBreakpoint } from '@cdab/scania/qpr/hooks'
import {
  auditsController,
  deviationsController
} from '@cdab/scania/qpr/offline/controllers'
import type { DeviationModel } from '@cdab/scania/qpr/offline/models'
import type { AuditPoint, FileData, UserFile } from '@cdab/scania/qpr/schema'
import { freeMemory } from '@cdab/scania/qpr/utils'
import { capitalizeFirstLetter, fileIsImage } from '@cdab/utils'
import {
  TdsCheckbox,
  TdsDatetime,
  TdsDropdown,
  TdsDropdownOption,
  TdsIcon,
  TdsSpinner,
  TdsTextField,
  TdsTextarea
} from '@scania/tegel-react'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import {
  EmptyScreenWrapper,
  FormRow,
  StyledBlock,
  StyledDiv
} from './new-deviation.styles'
import type { NewDeviationProps, UseAuditState } from './new-deviation.types'

const useAudit = (auditId: number) => {
  const [state, setState] = useState<UseAuditState>({
    status: 'loading',
    audit: undefined
  })

  useEffect(() => {
    async function asyncEffect() {
      const { audit } = await auditsController.GetAudit(auditId)

      setState({
        status: 'success',
        audit
      })
    }
    asyncEffect()
  }, [auditId])

  return state
}

export const NewDeviationDraft = observer(
  (props: Readonly<NewDeviationProps>) => {
    const { t } = useTranslation(['common', 'audit'])
    const { deviation } = props
    const auditState = useAudit(props.auditId)
    const isLg = useCssVariableBreakpoint('--tds-grid-lg')

    const ctx = useDeviation()

    const files =
      auditState.audit?.deviations.find(
        d => d.clientGuid === deviation?.clientGuid
      )?.files ?? []

    const selectedFilesToUpload: UserFile[] =
      (ctx?.form?.watch('files') as UserFile[]) ?? []
    const selectedFilesToDelete: string[] =
      (ctx?.form?.watch('filesToDelete') as string[]) ?? []
    let auditPoints: AuditPoint[] = []

    if (auditState.audit) {
      auditPoints = auditState.audit.auditPoints
        .slice()
        .sort((a, b) => a.sortOrder - b.sortOrder)
    }

    useEffect(() => {
      if (
        deviation?.clientGuid &&
        auditState.audit?.deviations.find(
          d => d.clientGuid === deviation?.clientGuid
        )
      ) {
        if (selectedFilesToUpload.length > 0) {
          deviationsController.AddFiles(
            deviation?.auditId,
            deviation?.clientGuid,
            selectedFilesToUpload.map(({ file }) => file)
          )
          ctx?.form?.setValue('files', [])
        }
        if (selectedFilesToDelete.length > 0) {
          selectedFilesToDelete.forEach(fileToDelete => {
            deviationsController.DeleteFile(
              deviation?.auditId,
              deviation?.clientGuid,
              fileToDelete
            )
          })
          ctx?.form?.setValue('filesToDelete', [])
        }
      }
    }, [
      selectedFilesToUpload,
      selectedFilesToDelete,
      ctx?.form,
      auditState.audit,
      props.auditId,
      auditState,
      deviation,
      files
    ])

    useEffect(() => {
      return () => {
        freeMemory(selectedFilesToUpload)
      }
    }, [selectedFilesToUpload])

    const onUploadFilesClick = (fileList: FileList) => {
      const files = Array.from(fileList)

      if (files.length === 0) return

      const inputFiles: UserFile[] = Array.from(files).map(file => ({
        file,
        id: uuidv4(),
        url: URL.createObjectURL(file)
      }))

      const nextFiles = [...selectedFilesToUpload, ...inputFiles]

      ctx?.form?.setValue('files', nextFiles)
    }

    const onDeleteSelectedFile = (fileId: FileData['id']) => {
      // TODO: Add translation
      if (!window.confirm('Are you sure you want to delete this file?')) return

      const selectedFileIndex = selectedFilesToUpload.findIndex(
        f => f.id === fileId
      )

      if (selectedFileIndex === -1) return

      const nextFiles = selectedFilesToUpload.slice()
      const deletedFiles = nextFiles.splice(selectedFileIndex, 1)

      freeMemory(deletedFiles)

      ctx?.form?.setValue('files', nextFiles)
    }

    const onDeleteFileClick = (fileId: UserFile['id']) => {
      if (!window.confirm('Are you sure you want to delete this file?')) return

      const currentValues = ctx?.form?.getValues('filesToDelete') ?? []
      currentValues.push(fileId)

      ctx?.form?.setValue('filesToDelete', currentValues)
    }

    if (auditState.status === 'loading') return <DelayedSpinner />

    const filesBeingUploaded: DeviationModel['files'] = []
    const uploadedFiles: DeviationModel['files'] = []

    files
      ?.filter(
        ({ id }) =>
          (ctx?.form?.getValues('filesToDelete') ?? []).includes(id) === false
      )
      .forEach(file => {
        if (file.isUploaded) {
          uploadedFiles.push(file)
        } else {
          filesBeingUploaded.push(file)
        }
      })

    const noFilesPresent =
      selectedFilesToUpload.length === 0 &&
      filesBeingUploaded.length === 0 &&
      uploadedFiles.length === 0 &&
      !deviation?.isGettingFiles

    return (
      <StyledDiv>
        <Column padding={isLg} width={12} lg={7} className='tds-on-white-bg'>
          <StyledBlock
            divider
            description={t('deviation.description', { ns: 'audit' })}
            variant='primary'
            header={t('deviation.title', { ns: 'audit' })}
          >
            <TdsCheckbox
              {...ctx?.form?.register('withActionPlan', {
                setValueAs: v => v === 'true' || v === true
              })}
              checked={!ctx?.form?.watch('withActionPlan')}
              onTdsChange={() => {
                ctx?.form?.setValue(
                  'withActionPlan',
                  !ctx?.form?.getValues('withActionPlan')
                )
              }}
            >
              <div slot='label'>
                {capitalizeFirstLetter(
                  t('deviation-without-action-plan', { ns: 'audit' })
                )}
              </div>
            </TdsCheckbox>
            {!props.defaultAuditPoint && (
              <FormRow>
                <TdsDropdown
                  {...ctx?.form?.register('auditPointId')}
                  size='sm'
                  filter
                  modeVariant='primary'
                  multiselect={false}
                  label={capitalizeFirstLetter(t('audit-point'))}
                  placeholder={t('select-audit-point', { ns: 'audit' })}
                  disabled={auditState.audit.isReadonly}
                  onTdsChange={e => {
                    if (!e.target.value) {
                      return
                    }
                    const auditPointId = Number(e.target.value)
                    const auditPointNo = auditPoints.find(
                      ap => ap.id === auditPointId
                    )?.auditPointNo

                    ctx?.form?.setValue('auditPointNumber', auditPointNo)
                    ctx?.form?.setValue('auditPointId', auditPointId.toString())
                  }}
                  defaultValue={
                    ctx?.form?.getValues('auditPointId') > 0
                      ? ctx?.form?.getValues('auditPointId')
                      : undefined
                  }
                  helper={
                    props.showErrorHelpers &&
                    ctx?.form?.formState.errors['auditPointNumber']?.message
                      ? t('validation-required')
                      : undefined
                  }
                  error={
                    props.showErrorHelpers &&
                    ctx?.form?.formState.errors['auditPointNumber']?.message !==
                      undefined
                  }
                >
                  {auditPoints.map(({ id, auditPointNo, translationGuid }) => (
                    <TdsDropdownOption key={id} value={id?.toString() || ''}>
                      {`${auditPointNo.toString()} ${parse(
                        t(translationGuid, {
                          ns: 'audit'
                        })
                      )}`}
                    </TdsDropdownOption>
                  ))}
                </TdsDropdown>
              </FormRow>
            )}
            <FormRow>
              <TdsTextarea
                {...ctx?.form?.register('deviation')}
                cols={2}
                helper={
                  props.showErrorHelpers
                    ? ctx?.form?.formState.errors[
                        'deviation'
                      ]?.message?.toString()
                    : undefined
                }
                maxLength={1000}
                name='deviation'
                label={`${capitalizeFirstLetter(t('deviation'))}*`}
                labelPosition='outside'
                onTdsInput={e =>
                  ctx?.form?.setValue('deviation', e.target.value)
                }
                placeholder={capitalizeFirstLetter(t('deviation'))}
                state={
                  props.showErrorHelpers &&
                  ctx?.form?.formState.errors['deviation']
                    ? 'error'
                    : undefined
                }
                value={ctx?.form?.getValues('deviation') ?? ''}
              />
            </FormRow>

            {ctx?.form?.getValues('withActionPlan') && (
              <>
                <FormRow>
                  <TdsTextarea
                    {...ctx?.form?.register('action')}
                    cols={2}
                    helper={
                      props.showErrorHelpers
                        ? ctx?.form?.formState.errors[
                            'action'
                          ]?.message?.toString()
                        : undefined
                    }
                    maxLength={1000}
                    label={`${capitalizeFirstLetter(
                      t('proposed-action', { ns: 'audit' })
                    )}*`}
                    labelPosition='outside'
                    name='action'
                    onTdsInput={e =>
                      ctx?.form?.setValue('action', e.target.value)
                    }
                    placeholder={t('proposed-action', { ns: 'audit' })}
                    state={
                      props.showErrorHelpers &&
                      ctx?.form?.formState.errors['action']
                        ? 'error'
                        : undefined
                    }
                    value={ctx?.form?.getValues('action') ?? ''}
                  />
                </FormRow>
                <FormRow>
                  <TdsTextField
                    {...ctx?.form?.register('assignee')}
                    helper={
                      props.showErrorHelpers
                        ? ctx?.form?.formState.errors[
                            'assignee'
                          ]?.message?.toString()
                        : undefined
                    }
                    size='sm'
                    maxLength={100}
                    name='assignee'
                    label={`${capitalizeFirstLetter(t('responsible'))}*`}
                    labelPosition='outside'
                    onTdsInput={e =>
                      ctx?.form?.setValue('assignee', e.target.value)
                    }
                    placeholder={t('responsible', { ns: 'audit' })}
                    state={
                      props.showErrorHelpers &&
                      ctx?.form?.formState.errors['assignee']
                        ? 'error'
                        : undefined
                    }
                    value={ctx?.form?.getValues('assignee')}
                  />
                </FormRow>
                <FormRow>
                  <TdsDatetime
                    {...ctx?.form?.register('due')}
                    min={undefined}
                    max={undefined}
                    helper={
                      props.showErrorHelpers
                        ? ctx?.form?.formState.errors[
                            'due'
                          ]?.message?.toString()
                        : undefined
                    }
                    size='sm'
                    name='due'
                    label={`${capitalizeFirstLetter(t('due-date'))}*`}
                    labelPosition='outside'
                    type='date'
                    onTdsChange={e => {
                      ctx?.form?.setValue('due', e.target.value)
                    }}
                    state={
                      props.showErrorHelpers &&
                      ctx?.form?.formState.errors['due']
                        ? 'error'
                        : undefined
                    }
                    value={ctx?.form?.getValues('due') ?? ''} //Not possible to be undefined when rerendering
                  />
                </FormRow>
                <FormRow>
                  <TdsDatetime
                    {...ctx?.form?.register('completed')}
                    min={undefined}
                    max={undefined}
                    helper={
                      props.showErrorHelpers
                        ? ctx?.form?.formState.errors[
                            'completed'
                          ]?.message?.toString()
                        : undefined
                    }
                    size='sm'
                    type='date'
                    name='completed'
                    label={capitalizeFirstLetter(t('completed'))}
                    labelPosition='outside'
                    onTdsChange={e =>
                      ctx?.form?.setValue('completed', e.target.value)
                    }
                    state={
                      props.showErrorHelpers &&
                      ctx?.form?.formState.errors['completed']
                        ? 'error'
                        : undefined
                    }
                    value={ctx?.form?.getValues('completed') ?? ''} //Not possible to be undefined when rerendering
                  />
                </FormRow>
              </>
            )}
          </StyledBlock>
        </Column>

        <Column width={12} lg={5} padding={isLg}>
          <Block
            className={classNames({ 'tds-u-mt1': !isLg })}
            divider
            variant='primary'
            header={
              !noFilesPresent
                ? capitalizeFirstLetter(t('evidence', { ns: 'audit' }))
                : ''
            }
          >
            {!noFilesPresent && (
              <UploadFileButton
                text={capitalizeFirstLetter(t('select-file', { ns: 'common' }))}
                size='sm'
                onUploadFiles={onUploadFilesClick}
              />
            )}
            <div>
              {deviation?.isGettingFiles ? (
                <TdsSpinner />
              ) : (
                uploadedFiles.length > 0 && (
                  <AttachmentList
                    modeVariant='secondary'
                    files={uploadedFiles}
                    onDeleteAttachment={onDeleteFileClick}
                    disableDelete={false}
                  />
                )
              )}
            </div>
            {selectedFilesToUpload.length > 0 && (
              <div>
                <AttachmentList
                  modeVariant='secondary'
                  fileType='all'
                  files={selectedFilesToUpload.map(({ file, id, url }) => ({
                    fileName: file.name,
                    id,
                    isImage: fileIsImage(file),
                    isUploaded: false,
                    uploadProgress: 0,
                    url
                  }))}
                  hideUploadProgress
                  onDeleteAttachment={onDeleteSelectedFile}
                  disableDelete={false}
                />
              </div>
            )}
            {filesBeingUploaded.length > 0 && (
              <div>
                <AttachmentList
                  modeVariant='secondary'
                  files={filesBeingUploaded}
                  disableDelete
                />
              </div>
            )}
            {noFilesPresent && (
              <EmptyScreenWrapper>
                <EmptyScreen
                  title={t('no-evidence-exists', { ns: 'audit' })}
                  description='You have not added any evidence to this deviation.'
                  icon={<TdsIcon name='warning' size='lg' />}
                  callToAction={{
                    type: 'button',
                    component: (
                      <UploadFileButton
                        text={t('add-evidence', { ns: 'audit' })}
                        size='sm'
                        onUploadFiles={onUploadFilesClick}
                      />
                    )
                  }}
                />
              </EmptyScreenWrapper>
            )}
          </Block>
        </Column>
      </StyledDiv>
    )
  }
)
